import PricingQuoteCard, { isVolumeQuote } from "./PricingQuoteCard";
import CollapsibleMatrix from "./CollapsibleMatrix";
import InlineMaterialPicker, { parseMaterialPicker } from "./InlineMaterialPicker";
import { parseQuoteBlock, stripQuoteBlock } from "../lib/session";

// Tiny safe inline markdown renderer.
// Supports: **bold**, __bold__, *italic*, _italic_, `code`, and preserves newlines.
// Escapes HTML first, so user/LLM content can never inject tags.
const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const renderInlineMarkdown = (text) => {
  let out = escapeHtml(text);
  // code spans first to protect content inside
  out = out.replace(/`([^`\n]+)`/g, '<code class="px-1 py-0.5 rounded bg-black/5 text-[13px]">$1</code>');
  // bold: **text** or __text__
  out = out.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");
  // italic: *text* or _text_  (avoid matching ** by requiring non-* on both sides)
  out = out.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  out = out.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, "$1<em>$2</em>");
  return out;
};

const ChatBubble = ({
  role,
  content,
  messageId,
  onOrderNow,
  onEmailQuote,
  onRequote,
  productsById,
  papersById,
  isLastAssistant,
  isLatestAssistant,
  sessionId,
  bigheadsFatheads,
  onBigheadsChange,
  onPickMaterial,
  onOpenMaterial,
}) => {
  const isUser = role === "user";
  const quote = !isUser ? parseQuoteBlock(content) : null;
  const stripped = !isUser ? stripQuoteBlock(content) : content;
  // Detect an inline material-picker directive emitted by the LLM.
  const { text: displayText, picker: pickerCategory } = !isUser
    ? parseMaterialPicker(stripped)
    : { text: stripped, picker: null };
  // Render the picker only on the most recent assistant turn — older bubbles
  // keep the text but drop the live picker (the customer already moved past).
  const showPicker = !isUser && pickerCategory && isLatestAssistant && !quote;

  return (
    <div
      className={`bubble-enter flex ${isUser ? "justify-end" : "justify-start"} mb-4`}
      data-testid={`chat-bubble-${role}`}
    >
      <div className={`max-w-[85%] sm:max-w-[80%] flex flex-col gap-3 ${isUser ? "items-end" : "items-start"}`}>
        {!isUser && (
          <div className="overline" style={{ color: "var(--ups-subtext)" }}>
            Roswell Print Center
          </div>
        )}
        {displayText && (
          <div
            className={`px-4 py-3 rounded-sm text-[15px] leading-relaxed whitespace-pre-wrap ${
              isUser ? "" : "card-swiss shadow-sm"
            }`}
            style={
              isUser
                ? { background: "var(--ups-muted)", color: "var(--ups-ink)", border: "1px solid var(--ups-border)" }
                : { background: "#fff" }
            }
            data-testid={`chat-bubble-text-${role}`}
            dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(displayText) }}
          />
        )}
        {showPicker && (
          <div className="w-full max-w-full">
            <InlineMaterialPicker
              category={pickerCategory}
              onPick={(m) => onPickMaterial?.(m)}
              onOpenFull={(m) => onOpenMaterial?.(m)}
            />
          </div>
        )}
        {quote && (
          <PricingQuoteCard
            quote={quote}
            onOrderNow={() =>
              onOrderNow({
                quote,
                content,
                messageId,
                fatheadIds: (bigheadsFatheads || []).filter(Boolean).map((f) => f.fathead_id),
              })
            }
            onEmailQuote={onEmailQuote}
            onRequote={onRequote ? (lineIndex, patch) => onRequote({ quote, lineIndex, patch }) : null}
            productsById={productsById}
            papersById={papersById}
            disabled={!isLastAssistant}
            sessionId={sessionId}
            bigheadsFatheads={bigheadsFatheads}
            onBigheadsChange={(next) => onBigheadsChange?.(messageId, next)}
          />
        )}
        {quote && isVolumeQuote(quote, productsById) && quote.product_id && (
          <CollapsibleMatrix
            sessionId={sessionId}
            productId={quote.product_id}
            productName={quote.product_name || "Product"}
            defaultSizeId={quote.size_id}
            defaultPaperId={quote.paper_id}
          />
        )}
      </div>
    </div>
  );
};

export default ChatBubble;
